/**
 * Singleton Auth Hook
 * WHY: Prevents session loss during route navigation by maintaining a single
 * source of truth for auth state across all component mounts/unmounts.
 * 
 * The Supabase client persists sessions in localStorage, but getSession() is async.
 * When AppLayout remounts on route changes, a brief null-session window causes
 * unwanted redirects to /auth. This hook initializes auth ONCE at module level
 * and shares state across all consumers via useSyncExternalStore.
 */

import { useSyncExternalStore, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";
import { adsSignupIfNew } from "@/lib/adsConversion";

interface AuthState {
  session: Session | null;
  user: User | null;
  isReady: boolean;
}

// ── Module-level singleton state ──
let state: AuthState = {
  session: null,
  user: null,
  isReady: false,
};

const listeners = new Set<() => void>();

function emitChange() {
  // Create new object reference so useSyncExternalStore detects change
  state = { ...state };
  listeners.forEach((l) => l());
}

function getSnapshot(): AuthState {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ── Initialize auth ONCE when module loads ──
let initialized = false;
let getSessionResolved = false;

// One row per signed-in person per day in app_active_days (cc_tasks row 42): the
// funnel's "back within 7 days" step. At most one call a day from this browser, and a
// failure changes nothing for the person.
function recordActiveDay() {
  try {
    const today = new Date().toISOString().slice(0, 10);
    if (window.localStorage.getItem("mf_active_day") === today) return;
    window.localStorage.setItem("mf_active_day", today);
    void supabase.rpc("record_app_day" as never).then(() => undefined, () => undefined);
  } catch { /* storage or network unavailable: not counted */ }
}

function initAuth() {
  if (initialized) return;
  initialized = true;

  // 1. Set up onAuthStateChange FIRST (before getSession)
  supabase.auth.onAuthStateChange((_event, session) => {
    state.session = session;
    state.user = session?.user ?? null;
    // Only mark ready if getSession has already resolved, OR if we got a real session
    // This prevents the race: onAuthStateChange fires with null before getSession restores the token
    if (getSessionResolved || session) {
      state.isReady = true;
    }
    emitChange();
    if (session) recordActiveDay();
    // Google Ads signup_completed (cc_tasks row 88): ad visitors only, once, new accounts only.
    if (session) adsSignupIfNew(session.user?.created_at);
  });

  // 2. Restore session from storage — this is the authoritative source on page load
  supabase.auth.getSession().then(({ data: { session } }) => {
    getSessionResolved = true;
    state.session = session;
    state.user = session?.user ?? null;
    state.isReady = true;
    emitChange();
    if (session) recordActiveDay();
    // Google Ads signup_completed (cc_tasks row 88): ad visitors only, once, new accounts only.
    if (session) adsSignupIfNew(session.user?.created_at);
  });

  // 3. Handle ephemeral sessions — sign out on tab close if user opted out of persistence
  window.addEventListener("beforeunload", () => {
    if (window.localStorage.getItem("mf_session_ephemeral") === "true") {
      // Clear Supabase session from storage so it doesn't persist
      // We use localStorage keys that Supabase uses internally
      const keys = Object.keys(window.localStorage);
      for (const key of keys) {
        if (key.startsWith("sb-") && key.endsWith("-auth-token")) {
          window.localStorage.removeItem(key);
        }
      }
    }
  });
}

// Initialize immediately on import
initAuth();

/**
 * Hook that returns current auth state.
 * Safe to call from any component — state is shared across all instances.
 */
export function useAuth() {
  const authState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return authState;
}
